import React, { forwardRef } from "react";
import styled from "@emotion/styled";
import { opacity } from "../../utils/color";
import { useTheme, keyframes } from "@emotion/react";
import { Loading } from "@bale/icons";
import { FontFamily } from "../../constants";
export var ButtonVariant;
(function (ButtonVariant) {
    ButtonVariant["Filled"] = "Filled";
    ButtonVariant["Outlined"] = "Outlined";
})(ButtonVariant || (ButtonVariant = {}));
export var ButtonSize;
(function (ButtonSize) {
    ButtonSize["Full"] = "Full";
    ButtonSize["XLarge"] = "XLarge";
    ButtonSize["Large"] = "Large";
    ButtonSize["Medium"] = "Medium";
    ButtonSize["Small"] = "Small";
    ButtonSize["XSmall"] = "XSmall";
    ButtonSize["Tiny"] = "Tiny";
})(ButtonSize || (ButtonSize = {}));
export var BorderRadius;
(function (BorderRadius) {
    BorderRadius["None"] = "None";
    BorderRadius["Normal"] = "Normal";
    BorderRadius["Rounded"] = "Rounded";
})(BorderRadius || (BorderRadius = {}));
export const BaseButtonWrapper = styled.button `
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${FontFamily};
  line-height: 1;
  cursor: pointer;
  min-width: ${({ size, insideInput, fullWidth }) => fullWidth
    ? "100%"
    : insideInput
        ? "120px"
        : {
            [ButtonSize.Full]: "360px",
            [ButtonSize.XLarge]: "336px",
            [ButtonSize.Large]: "296px",
            [ButtonSize.Medium]: "225px",
            [ButtonSize.Small]: "160px",
            [ButtonSize.XSmall]: "100px",
            [ButtonSize.Tiny]: "78px",
        }[size]};

  padding: ${({ size, insideInput }) => insideInput
    ? "12px 13px"
    : {
        [ButtonSize.Full]: "18px",
        [ButtonSize.XLarge]: "18px",
        [ButtonSize.Large]: "18px",
        [ButtonSize.Medium]: "14px",
        [ButtonSize.Small]: "13px",
        [ButtonSize.XSmall]: "8px",
        [ButtonSize.Tiny]: "7px",
    }[size]};

  font-size: ${({ size }) => ({
    [ButtonSize.Full]: "18px",
    [ButtonSize.XLarge]: "18px",
    [ButtonSize.Large]: "18px",
    [ButtonSize.Medium]: "16px",
    [ButtonSize.Small]: "14px",
    [ButtonSize.XSmall]: "14px",
    [ButtonSize.Tiny]: "13px",
})[size]};

  color: ${({ varient, theme, disable, color }) => {
    return disable
        ? theme.colors.dark[100]
        : {
            [ButtonVariant.Outlined]: color !== null && color !== void 0 ? color : theme.colors.primary[50],
            [ButtonVariant.Filled]: theme.colors.light.on_primary,
        }[varient];
}};

  background-color: ${({ varient, theme, disable, color, transparent }) => transparent
    ? "transparent"
    : disable
        ? theme.colors.light[30]
        : {
            [ButtonVariant.Filled]: color !== null && color !== void 0 ? color : theme.colors.primary[50],
            [ButtonVariant.Outlined]: theme.colors.light[0],
        }[varient]};

  border: ${({ varient, theme, color, borderWidth }) => ({
    [ButtonVariant.Outlined]: `${borderWidth !== null && borderWidth !== void 0 ? borderWidth : 1}px solid ${color !== null && color !== void 0 ? color : theme.colors.primary[50]}`,
    [ButtonVariant.Filled]: "none",
})[varient]};

  border-radius: ${({ borderRadius, size }) => {
    if (borderRadius === BorderRadius.None) {
        return "0";
    }
    else if (borderRadius === BorderRadius.Normal) {
        return {
            [ButtonSize.Full]: "0",
            [ButtonSize.XLarge]: "14px",
            [ButtonSize.Large]: "15px",
            [ButtonSize.Medium]: "13px",
            [ButtonSize.Small]: "12px",
            [ButtonSize.XSmall]: "10px",
            [ButtonSize.Tiny]: "8px",
        }[size];
    }
    else {
        return "100px";
    }
}};

  &:hover {
    background-color: ${({ varient, theme, disable, color }) => disable
    ? "none"
    : {
        [ButtonVariant.Outlined]: opacity(color !== null && color !== void 0 ? color : theme.colors.primary[40], 0.1),
        [ButtonVariant.Filled]: color !== null && color !== void 0 ? color : theme.colors.primary[40],
    }[varient]};
  }
`;
const rotateAnimation = keyframes `
  12.5% {
    transform: rotate(45deg);
  }
  25% {
    transform: rotate(90deg);
  }
  37.5% {
    transform: rotate(135deg);
  }
  50% {
    transform: rotate(180deg);
  }
  62.5% {
    transform: rotate(225deg);
  }
  75% {
    transform: rotate(270deg);
  }
  87.5% {
    transform: rotate(315deg);
  }
  100% {
    transform: rotate(360deg);
  }`;
const LoadingWrapper = styled(Loading) `
  animation: ${rotateAnimation} 0.75s step-end infinite;
`;
/** @deprecated use NewButton instead */
export const Button = forwardRef((props, ref) => {
    var _a, _b;
    const theme = useTheme();
    const variant = (_a = props.variant) !== null && _a !== void 0 ? _a : ButtonVariant.Filled;
    return (React.createElement(BaseButtonWrapper, { ref: ref, varient: variant, size: props.size, insideInput: props.insideInput, borderRadius: (_b = props.borderRadius) !== null && _b !== void 0 ? _b : BorderRadius.Normal, disable: props.disable || props.disableThemeOnly, onClick: props.onClick, className: props.className, "aria-label": props.text, color: props.color, "data-testid": props["data-testid"], style: Object.assign({}, props.style), disabled: props.disable, id: props.id, transparent: props.transparent, fullWidth: props.fullWidth, borderWidth: props.borderWidth },
        props.loading && (React.createElement(LoadingWrapper, { size: {
                [ButtonSize.Full]: 24,
                [ButtonSize.XLarge]: 23,
                [ButtonSize.Large]: 22,
                [ButtonSize.Medium]: 20,
                [ButtonSize.Small]: 19,
                [ButtonSize.XSmall]: 17,
                [ButtonSize.Tiny]: 15,
            }[props.size], color: variant === ButtonVariant.Filled
                ? theme.colors.light.on_primary
                : theme.colors.primary[50] })),
        !props.loading && (React.createElement(React.Fragment, null,
            props.icon,
            props.text ? props.text : props.children))));
});
//# sourceMappingURL=BaseButton.js.map